/**
 * Domínio: validação do cadastro de contatos pelo admin — C-75 + GUPM-202.
 * -------------------------------------------------------------------
 * TS puro (sem Zod: o projeto não tem a dependência). Os limites são os MESMOS
 * dos CHECKs do banco (migrations 20261013120000 e 20261015140000): a tela recusa
 * antes de enviar e o banco recusa quem chamar a API direto.
 *
 * GUPM-202 (Gabriel, 14/10):
 * - as funções são etiquetas (várias por contato);
 * - "é de emergência" é uma marcação, não mais uma categoria;
 * - contato NOVO exige todos os dados (nome, ao menos 1 função, empresa, telefone e
 *   e-mail); vinculado a um usuário do app, nome e e-mail vêm do usuário;
 * - contato EXISTENTE incompleto não é forçado: a edição só exige o que ele já tinha;
 * - disponibilidade é sempre opcional (D-13).
 *
 * Placeholders de produção continuam aceitos na edição: telefone vazio = contato
 * pendente, exibido como "[A definir]" e sem link de ligação.
 */

/** Espelho dos CHECKs `contacts_*_tam` / `contacts_*_fmt` / `contacts_funcoes_chk`. */
export const LIMITES_CONTATO = {
  name: 120,
  /** Cada função (etiqueta). */
  funcao: 120,
  /** Quantidade de funções por contato. */
  funcoes: 10,
  display: 120,
  empresa: 120,
  disponibilidade: 200,
  phone: 30,
  email: 254,
} as const;

/** Mesmo padrão do CHECK `contacts_phone_fmt`. */
const TELEFONE_RE = /^[0-9+() -]*$/;
/** Mesmo padrão do CHECK `contacts_email_fmt`. */
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const EXIBICAO_PENDENTE = '[A definir]';

/** O que o formulário entrega: texto como digitado, funções como etiquetas. */
export interface ContatoEntrada {
  funcoes: string[];
  name: string;
  phone: string;
  display: string;
  email: string;
  empresa: string;
  disponibilidade: string;
  is_emergencia: boolean;
  /** Usuário do app vinculado, ou `null` ("Não é usuário do app"). */
  app_user_id: string | null;
}

/** O que vai ao banco. */
export interface ContatoNormalizado {
  funcoes: string[];
  /** Com vínculo: o nome do usuário, gravado como reserva. */
  name: string;
  /** `''` quando pendente (a coluna é NOT NULL). */
  phone: string;
  display: string;
  /** Com vínculo: o e-mail do usuário, gravado como reserva. */
  email: string | null;
  empresa: string | null;
  disponibilidade: string | null;
  is_emergencia: boolean;
  app_user_id: string | null;
}

/** Contato como está gravado (para a edição). */
export type ContatoGravado = Omit<ContatoNormalizado, 'funcoes'> & { funcoes: readonly string[] };

export type CampoContato = 'funcoes' | 'name' | 'phone' | 'display' | 'email' | 'empresa' | 'disponibilidade';

export type ResultadoValidacao =
  | { ok: true; valor: ContatoNormalizado }
  | { ok: false; erros: Partial<Record<CampoContato, string>> };

/** Nome e e-mail do usuário do app escolhido no formulário. */
export interface UsuarioVinculado {
  name: string;
  email: string | null;
}

export interface OpcoesValidacao {
  /** Contato gravado: presente = edição; ausente = cadastro novo. */
  gravado?: ContatoGravado | null;
  /** Usuário do app vinculado (fonte de nome e e-mail). */
  usuario?: UsuarioVinculado | null;
}

function maximo(n: number): string {
  return `Máximo de ${n} caracteres`;
}

/**
 * Etiquetas de função como vão ao banco: sem espaço sobrando, sem vazias e sem
 * repetição (comparação sem diferenciar maiúscula; fica a primeira grafia).
 */
export function normalizarFuncoes(lista: readonly string[]): string[] {
  const vistas = new Set<string>();
  const saida: string[] = [];
  for (const bruta of lista) {
    const f = bruta.trim();
    const chave = f.toLocaleLowerCase('pt-BR');
    if (!f || vistas.has(chave)) continue;
    vistas.add(chave);
    saida.push(f);
  }
  return saida;
}

/**
 * Funções de um `role` legado, partido em " / " — espelho de
 * `contacts_funcoes_do_role` (migration 20261015140000). " / " DENTRO de
 * parênteses é parte da função: "Suporte GeoGuard (WhatsApp / e-mail)" é uma só.
 */
export function funcoesDoRole(role: string): string[] {
  const partes: string[] = [];
  let atual = '';
  let profundidade = 0;
  for (let i = 0; i < role.length; i++) {
    const c = role[i]!;
    if (c === '(') profundidade++;
    else if (c === ')') profundidade = Math.max(0, profundidade - 1);
    if (profundidade === 0 && role.startsWith(' / ', i)) {
      partes.push(atual);
      atual = '';
      i += 2;
      continue;
    }
    atual += c;
  }
  partes.push(atual);
  return normalizarFuncoes(partes);
}

/**
 * Telefone formatado para exibição, no padrão das exibições já cadastradas em
 * prod: sem o 55 do país, `(DD) 9XXXX-XXXX` (celular), `(DD) XXXX-XXXX` (fixo) e
 * `0800 XXX-XXXX`. Formato que não reconhece (ramal, internacional) fica como
 * digitado — melhor mostrar o número cru do que um número errado.
 */
export function formatarTelefone(phone: string): string {
  const bruto = phone.trim();
  let d = bruto.replace(/\D/g, '');
  if (/^0800\d{7}$/.test(d)) return `0800 ${d.slice(4, 7)}-${d.slice(7)}`;
  // Outro país: não é formato nacional.
  if (bruto.startsWith('+') && !bruto.startsWith('+55')) return bruto;
  // O 55 só é do país quando sobra um número nacional completo (DDD + 8/9
  // dígitos); assim o DDD 55 (RS) sem código do país não perde os dígitos.
  if (/^55\d{10,11}$/.test(d)) d = d.slice(2);
  if (/^\d{2}9\d{8}$/.test(d)) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (/^\d{10}$/.test(d)) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return bruto;
}

/**
 * Exibição que vai ao banco. Se o telefone mudou e a exibição ficou como estava
 * (o admin só trocou o número), a exibição antiga é descartada e passa a valer o
 * telefone novo — senão o modal de emergência ligaria para um número e mostraria
 * outro (ou "[A definir]" com link ativo).
 */
function exibicaoFinal(entrada: ContatoEntrada, phone: string, anterior?: ContatoGravado | null): string {
  let display = entrada.display.trim();
  if (anterior && phone !== anterior.phone.trim() && display === anterior.display.trim()) display = '';
  return display || (phone ? formatarTelefone(phone) : EXIBICAO_PENDENTE);
}

/** Na edição, só é obrigatório o que o contato já tinha (D-5). */
function obrigatorio(gravado: ContatoGravado | null | undefined, valorGravado: string | null | undefined): boolean {
  return !gravado || (valorGravado ?? '').trim() !== '';
}

export function validarContato(entrada: ContatoEntrada, opcoes: OpcoesValidacao = {}): ResultadoValidacao {
  const { gravado, usuario } = opcoes;
  const vinculado = entrada.app_user_id !== null;
  const funcoes = normalizarFuncoes(entrada.funcoes);
  // Vinculado: nome e e-mail são os do usuário (o que estava na linha fica se o
  // usuário não estiver disponível, ex.: desativado).
  const name = (vinculado && usuario ? usuario.name : entrada.name).trim();
  const email = (vinculado && usuario ? usuario.email ?? '' : entrada.email).trim();
  const phone = entrada.phone.trim();
  const empresa = entrada.empresa.trim();
  const disponibilidade = entrada.disponibilidade.trim();
  const display = exibicaoFinal(entrada, phone, gravado);

  const erros: Partial<Record<CampoContato, string>> = {};

  if (funcoes.length === 0) erros.funcoes = 'Informe ao menos uma função';
  else if (funcoes.length > LIMITES_CONTATO.funcoes) erros.funcoes = `Máximo de ${LIMITES_CONTATO.funcoes} funções`;
  else if (funcoes.some((f) => f.length > LIMITES_CONTATO.funcao)) {
    erros.funcoes = `Cada função tem no máximo ${LIMITES_CONTATO.funcao} caracteres`;
  } else {
    // " / " separa funções no `role` que o front 1.12.0 lê e grava: a etiqueta voltaria
    // de lá partida em duas. Dentro de parênteses é parte do nome (`funcoesDoRole`).
    const composta = funcoes.find((f) => funcoesDoRole(f).length > 1);
    if (composta) erros.funcoes = `Uma função por etiqueta: separe "${composta}" em duas`;
  }

  // Vinculado, o nome já veio do usuário; vazio aqui só se não houver usuário nem reserva.
  if (!name) erros.name = 'Informe o nome';
  else if (name.length > LIMITES_CONTATO.name) erros.name = maximo(LIMITES_CONTATO.name);

  if (!TELEFONE_RE.test(phone)) erros.phone = 'Telefone aceita só números, espaço, +, ( ) e -';
  else if (phone.length > LIMITES_CONTATO.phone) erros.phone = maximo(LIMITES_CONTATO.phone);
  else if (!phone && obrigatorio(gravado, gravado?.phone)) erros.phone = 'Informe o telefone';

  if (display.length > LIMITES_CONTATO.display) erros.display = maximo(LIMITES_CONTATO.display);

  if (email) {
    if (email.length > LIMITES_CONTATO.email || !EMAIL_RE.test(email)) erros.email = 'E-mail inválido';
  } else if (!vinculado && obrigatorio(gravado, gravado?.email)) erros.email = 'Informe o e-mail';

  if (empresa.length > LIMITES_CONTATO.empresa) erros.empresa = maximo(LIMITES_CONTATO.empresa);
  else if (!empresa && obrigatorio(gravado, gravado?.empresa)) erros.empresa = 'Informe a empresa';

  if (disponibilidade.length > LIMITES_CONTATO.disponibilidade) {
    erros.disponibilidade = maximo(LIMITES_CONTATO.disponibilidade);
  }

  if (Object.keys(erros).length > 0) return { ok: false, erros };

  return {
    ok: true,
    valor: {
      funcoes,
      name,
      phone,
      display,
      email: email || null,
      empresa: empresa || null,
      disponibilidade: disponibilidade || null,
      is_emergencia: entrada.is_emergencia,
      app_user_id: entrada.app_user_id,
    },
  };
}
